import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const serviceList = [
  {
    id: 1,
    icon: 'ri-car-line',
    title: 'Şehir içi - şehirler arası',
    description: 'Şehrin her noktasına hızlı ve güvenli ulaşım. Malatya\'dan Türkiye\'nin her şehrine konforlu ve güvenli şehirler arası yolculuk hizmeti sunuyoruz.',
    image: '/1.jpeg',
  },
  {
    id: 2,
    icon: 'ri-time-line',
    title: '7/24 Kesintisiz Hizmet',
    description: 'Gece-gündüz kesintisiz hizmet, havalimanı transferleri, acil çağrılar. 7/24 ulaşabilirsiniz.',
    image: '/2.jpeg',
  },
  {
    id: 3,
    icon: 'ri-bank-card-line',
    title: 'Kredi Kartı ile Ödeme',
    description: 'Nakit taşıma derdi yok! Kredi kartı ve banka kartı ile kolayca ödeme yapabilirsiniz.',
    image: '/3.jpeg',
  },
];

export default function Services() {
  const { ref: titleRef, inView: titleInView } = useInView();
  const { ref: gridRef, inView: gridInView } = useInView(0.05);

  return (
    <section id="hizmetler" className="relative py-28 bg-white overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-400/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-400/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        {/* Başlık */}
        <div
          ref={titleRef}
          className="text-center mb-20"
          style={{
            opacity: titleInView ? 1 : 0,
            transform: titleInView ? 'translateY(0)' : 'translateY(50px)',
            transition: 'all 0.9s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <div className="inline-flex items-center space-x-2 bg-yellow-400 px-6 py-2.5 rounded-full mb-6 shadow-lg">
            <i className="ri-taxi-line text-black text-base" />
            <span className="text-black text-xs font-black uppercase tracking-widest whitespace-nowrap">Hizmetlerimiz</span>
          </div>
          <h2 className="text-6xl font-black text-black leading-tight mb-5">
            Size Nasıl Yardımcı<br />
            <span className="text-yellow-400">Olabiliriz?</span>
          </h2>
          <div
            className="h-1.5 bg-yellow-400 mx-auto mb-6"
            style={{
              width: titleInView ? '96px' : '0px',
              transition: 'width 1s ease 0.4s',
            }}
          />
          <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
            Malatya'nın her köşesinde güvenli ve konforlu ulaşım için yanınızdayız.
          </p>
        </div>

        {/* Kart Grid */}
        <div ref={gridRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {serviceList.map((service, i) => (
            <div
              key={service.id}
              className="group relative rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-500 cursor-pointer"
              style={{
                minHeight: '380px',
                opacity: gridInView ? 1 : 0,
                transform: gridInView ? 'translateY(0) scale(1)' : 'translateY(60px) scale(0.95)',
                transition: `all 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s`,
              }}
            >
              <div className="absolute inset-0 w-full h-full">
                <img
                  src={service.image}
                  alt={`Başharık Taksi Malatya - ${service.title} Hizmeti`}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  style={{ transform: 'translateY(-10%)' }}
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40 group-hover:from-black/95 group-hover:via-black/85 transition-all duration-500" />
              <div className="absolute inset-0 border-4 border-transparent group-hover:border-yellow-400 transition-all duration-500 rounded-2xl" />
              <div className="relative h-full flex flex-col justify-end p-7 z-10">
                <div className="w-14 h-14 flex items-center justify-center bg-yellow-400 rounded-xl mb-5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                  <i className={`${service.icon} text-2xl text-black`} />
                </div>
                <h3 className="text-2xl font-black text-white mb-3 group-hover:text-yellow-400 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed group-hover:text-white transition-colors duration-300">
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div
          className="mt-16 text-center"
          style={{
            opacity: gridInView ? 1 : 0,
            transform: gridInView ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.6s',
          }}
        >
          <a
            href="tel:05308920944"
            className="inline-flex items-center space-x-3 bg-black hover:bg-yellow-400 text-white hover:text-black px-12 py-5 rounded-full font-black text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 cursor-pointer whitespace-nowrap group"
          >
            <i className="ri-phone-fill text-yellow-400 group-hover:text-black text-2xl" />
            <span>Bilgi İçin Arayın: 0530 892 09 44</span>
          </a>
        </div>
      </div>
    </section>
  );
}
